import React from 'react';
import { motion } from 'framer-motion';
import { Landmark, ArrowRight, Percent, ShieldCheck, FileText, Banknote, Sparkles } from 'lucide-react';

export default function FiscalSummaryWaterfall({ calculations }) {
  const formatEuro = (val) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(val || 0);

  const steps = [
    { label: "Revenu Net Imposable", value: calculations.baseImposable, sub: `${calculations.parts} parts fiscales (QF : ${formatEuro(calculations.quotientFamilial)})`, color: "bg-info-500" },
    { label: "Impôt IR Brut", value: calculations.impotBrut, sub: `Application barème progressif 2024`, color: "bg-warning-500" },
    { label: "Réductions & Crédits", value: -calculations.totalReductionsCredits, sub: `Dons, emploi à domicile, scolarité`, color: "bg-success-500" },
    { label: "Impôt Net sur le Revenu (IR)", value: calculations.impotNetIR, sub: `Taux moyen : ${calculations.tauxMoyenImposition}%`, color: "bg-secondary" },
  ];

  return (
    <div className="space-y-6">
      {/* 4 KPIs Majeurs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* TMI */}
        <motion.div whileHover={{ y: -2 }} className="workspace-card p-5 bg-surface border border-workspace rounded-2xl shadow-hp-sm">
          <div className="flex items-center justify-between text-subtle mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider">Tranche Marginale (TMI)</span>
            <Percent className="w-4 h-4 text-secondary" />
          </div>
          <p className="text-3xl font-black text-secondary tracking-tight">{calculations.tmi} %</p>
          <p className="text-xs text-subtle mt-1">Tranche maximale d'imposition</p>
        </motion.div>

        {/* Impôt IR Net */}
        <motion.div whileHover={{ y: -2 }} className="workspace-card p-5 bg-surface border border-workspace rounded-2xl shadow-hp-sm">
          <div className="flex items-center justify-between text-subtle mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider">Impôt sur le Revenu Net</span>
            <Landmark className="w-4 h-4 text-warning-500" />
          </div>
          <p className="text-3xl font-black text-primary tracking-tight">{formatEuro(calculations.impotNetIR)}</p>
          <p className="text-xs text-subtle mt-1">Après déductions & crédits</p>
        </motion.div>

        {/* Prélèvements Sociaux */}
        <motion.div whileHover={{ y: -2 }} className="workspace-card p-5 bg-surface border border-workspace rounded-2xl shadow-hp-sm">
          <div className="flex items-center justify-between text-subtle mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider">Prélèvements Sociaux (17.2%)</span>
            <Banknote className="w-4 h-4 text-info-500" />
          </div>
          <p className="text-3xl font-black text-primary tracking-tight">
            {formatEuro(calculations.prelevementsSociauxDividendes + calculations.prelevementsSociauxFoncier)}
          </p>
          <p className="text-xs text-subtle mt-1">Sur RCM & revenus fonciers</p>
        </motion.div>

        {/* Charge Fiscale Totale */}
        <motion.div whileHover={{ y: -2 }} className="workspace-card p-5 bg-surface border border-workspace rounded-2xl shadow-hp-sm relative overflow-hidden">
          <div className="absolute top-0 right-0 w-2 h-full bg-danger-500" />
          <div className="flex items-center justify-between text-subtle mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider">Prélèvement Fiscal Total</span>
            <ShieldCheck className="w-4 h-4 text-danger-500" />
          </div>
          <p className="text-3xl font-black text-danger-600 tracking-tight">{formatEuro(calculations.totalImpotGlobal)}</p>
          <p className="text-xs text-subtle mt-1">IR + Dividendes + CSG/CRDS foncier</p>
        </motion.div>
      </div>

      {/* Waterfall / Décomposition de l'impôt */}
      <div className="workspace-card p-6 bg-surface border border-workspace rounded-2xl shadow-hp-sm">
        <h3 className="text-base font-bold text-primary pb-3 border-b border-workspace mb-5">
          Décomposition Analytique de la Pression Fiscale
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {steps.map((step, idx) => (
            <div key={idx} className="p-4 rounded-xl bg-surface-muted/30 border border-workspace flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${step.color}`} />
                  <span className="text-xs font-bold text-subtle uppercase">{step.label}</span>
                </div>
                <p className={`text-xl font-black ${step.value < 0 ? 'text-success-600' : 'text-primary'}`}>
                  {formatEuro(step.value)}
                </p>
              </div>
              <p className="text-[11px] text-subtle mt-3 pt-2 border-t border-workspace">{step.sub}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

